<template>
  <div class="bg-light border-right p-3" style="width: 300px; flex-shrink: 0; overflow-y: auto;">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="m-0">{{ $t("common.filter") }}</h5>
      <b-button variant="link" size="sm" class="p-0 text-decoration-none" @click="resetFilters">
        {{ $t("common.reset") }}
      </b-button>
    </div>

    <h5 class="mt-4">{{ $t("compliance-item.compliance") }}</h5>
    <!-- Compliance (Root) -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("compliance-item.compliance") }}</label>
      <b-form-select :value="filters.compliance" size="sm" @change="onComplianceSelect">
        <option value="">{{ $t("common.all") }}</option>
        <option v-for="opt in complianceList" :key="opt" :value="opt">{{ opt }}</option>
      </b-form-select>
    </div>

    <!-- Compliance Group -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("compliance-item-group.text") }}</label>
      <filter-select :placeholder="$t('common.select')" :selected="filters.complianceGroup" @filter="onComplianceGroupSearch" @changed="onComplianceGroupSelect"> </filter-select>
    </div>

    <!-- Compliance Item No -->
    <div class="form-group mb-2">
      <label>{{ $t("compliance-item.no") }}</label>
      <filter-select
        id="complianceItemNoFilter"
        ref="complianceItemNoFilter"
        :placeholder="$t('compliance-item.no')"
        :multiple="false"
        :selected="filters.complianceItemNo"
        @filter="complianceItemNoSearch"
        @changed="complianceItemNoSelect"
        track="name"
      />
    </div>

    <!-- Compliance Item Search -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("compliance-item.name") }}</label>
      <input type="text" class="form-control form-control-sm" :value="filters.complianceItemSearch" @input="updateFilter('complianceItemSearch', $event.target.value)" />
    </div>

    <!-- Compliance Item Description -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("compliance-item.description") }}</label>
      <input type="text" class="form-control form-control-sm" :value="filters.complianceItemDescription" @input="updateFilter('complianceItemDescription', $event.target.value)" />
    </div>

    <h5 class="mt-4">{{ $t("audit-control.finding.text") }}</h5>
    <!-- Finding No -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-control.finding.no") }}</label>
      <input type="text" class="form-control form-control-sm" :value="filters.no" @input="updateFilter('no', $event.target.value)" />
    </div>

    <!-- Finding Name -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-finding.name") }}</label>
      <input type="text" class="form-control form-control-sm" :value="filters.name" @input="updateFilter('name', $event.target.value)" />
    </div>

    <!-- Finding Category -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-finding.category.text") }}</label>
      <filter-select :placeholder="$t('common.select')" :selected="filters.category" @filter="onFindingCategorySearch" @changed="onFindingCategorySelect"> </filter-select>
    </div>

    <!-- Finding Type -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-finding.type.text") }}</label>
      <filter-select :placeholder="$t('common.select')" :selected="filters.type" @filter="onFindingTypeSearch" @changed="onFindingTypeSelect"> </filter-select>
    </div>

    <!-- Finding Class -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-finding.class.text") }}</label>
      <b-form-select :value="filters.class" size="sm" @change="updateFilter('class', $event)">
        <option :value="undefined">{{ $t("common.all") }}</option>
        <option value="NOTABLE_CONTROL_DEFICIENCY">{{ $t("audit-finding.class-options.notable_control_deficiency") }}</option>
        <option value="SIGNIFICANT_CONTROL_DEFICIENCY">{{ $t("audit-finding.class-options.significant_control_deficiency") }}</option>
        <option value="IMPROVEMENT_RECOMMENDATION">{{ $t("audit-finding.class-options.improvement_recommendation") }}</option>
      </b-form-select>
    </div>

    <!-- Finding Status -->
    <div class="form-group mb-2">
      <label class="font-weight-bold small mb-1">{{ $t("audit-finding.status.text") }}</label>
      <b-form-select :value="filters.status" size="sm" @change="updateFilter('status', $event)">
        <option :value="undefined">{{ $t("common.all") }}</option>
        <option value="AWAITING_APPROVAL">{{ $t("audit-finding.status.awaiting_approval") }}</option>
        <option value="REJECTED_EVALUATING">{{ $t("audit-finding.status.rejected_evaluating") }}</option>
        <option value="EVALUATING">{{ $t("audit-finding.status.evaluating") }}</option>
        <option value="ACTION_PLAN_MONITORING">{{ $t("audit-finding.status.action_plan_monitoring") }}</option>
        <option value="EXTENSION_APPROVAL">{{ $t("audit-finding.status.extension_approval") }}</option>
        <option value="COMPLETED">{{ $t("audit-finding.status.completed") }}</option>
        <option value="PASSIVE">{{ $t("audit-finding.status.passive") }}</option>
        <option value="CANCELED">{{ $t("audit-finding.status.canceled") }}</option>
      </b-form-select>
    </div>
  </div>
</template>

<script>
import { Compliances } from "@/modules/core/compliances/compliances";

export default {
  name: "OrganizationFindingFilterSidebar",
  data() {
    return {
      complianceList: Compliances,
    };
  },
  computed: {
    filters() {
      return this.$store.getters["organizationFinding/findingFilters"] || {};
    },
  },
  methods: {
    onComplianceSelect(value) {
      this.$refs.complianceItemNoFilter.reset();
      this.updateFilter("compliance", value);
    },
    async complianceItemNoSearch(value, callback) {
      const queryValue = { $regex: `.*${value}.*`, $options: "i" };
      const complianceFilter = this.filters.compliance;
      const response = await this.$store.dispatch(`libraryComplianceItem/paginateForSearch`, {
        options: { limit: 200, sort: { no: "asc" } },
        query: { ...(complianceFilter ? { compliance: { value: complianceFilter, isObject: true } } : {}), no: { value: queryValue, isObject: true } },
      });

      const options = response.data?.docs.map((i) => ({ name: { tr: i.no, en: i.no, de: i.no, fr: i.no } })) ?? [];
      options.sort((a, b) => (a.name.tr || "").localeCompare(b.name.tr || "", undefined, { numeric: true, sensitivity: "base" }));
      callback(options);
    },
    complianceItemNoSelect(value) {
      this.updateFilter("complianceItemNo", value || null);
    },
    updateFilter(key, val) {
      const newFilters = { ...this.filters, [key]: val };
      this.$store.commit("organizationFinding/findingFilters", newFilters);
      this.$emit("input", newFilters);
    },
    resetFilters() {
      const emptyFilters = {
        no: "",
        name: "",
        category: null,
        type: null,
        class: undefined,
        status: undefined,
        compliance: "",
        complianceGroup: null,
        complianceItemNo: null,
        complianceItemSearch: "",
        complianceItemDescription: "",
      };
      this.$store.commit("organizationFinding/findingFilters", emptyFilters);
      if (this.$refs.complianceItemNoFilter) this.$refs.complianceItemNoFilter.reset();
      this.$emit("input", emptyFilters);
      this.$emit("reset");
    },
    async onFindingTypeSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingType/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: {
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onFindingTypeSelect(item) {
      if (item) {
        this.updateFilter("type", item);
      } else {
        this.updateFilter("type", null);
      }
    },
    async onFindingCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceLibraryFindingCategory/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: {
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onFindingCategorySelect(item) {
      if (item) {
        this.updateFilter("category", item);
      } else {
        this.updateFilter("category", null);
      }
    },
    async onComplianceGroupSearch(value, callback) {
      const response = await this.$store.dispatch(`libraryComplianceItemGroup/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: {
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onComplianceGroupSelect(item) {
      if (item) {
        this.updateFilter("complianceGroup", item);
      } else {
        this.updateFilter("complianceGroup", null);
      }
    },
  },
};
</script>

<style scoped>
/* Custom scrollbar for sidebar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
}
::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
</style>
